<template>
  <div class="relative h-full w-full">
    <!-- Imgproxy thumbnail -->
    <img
      v-if="thumbnailUrl"
      :src="thumbnailUrl"
      class="h-full w-full object-contain"
      :alt="`Page ${pageNumber} thumbnail`"
      @load="onImageLoad"
      @error="onImageError"
    />

    <!-- Loading indicator -->
    <div
      v-if="isLoading && !thumbnailUrl"
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div class="flex flex-col items-center gap-2">
        <div
          class="h-4 w-4 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
        ></div>
        <span class="text-xs text-gray-500">Loading preview...</span>
      </div>
    </div>

    <!-- Error state -->
    <div
      v-if="error"
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div class="flex flex-col items-center gap-2 p-2">
        <FileText class="h-8 w-8 text-gray-400" />
        <span
          class="text-center text-xs text-gray-500"
          :title="error || ''"
        >
          Preview failed
        </span>
      </div>
    </div>

    <!-- Fallback for no document -->
    <div
      v-if="!thumbnailUrl && !isLoading && !error"
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div class="flex flex-col items-center gap-2">
        <FileText class="h-8 w-8 text-gray-400" />
        <span class="text-center text-xs text-gray-500">No preview available</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  /**
   * PDFPageThumbnail Component
   * A high-performance component for displaying PDF page thumbnails using imgproxy.
   *
   * Features:
   * - Uses server-side imgproxy for fast thumbnail generation
   * - Displays pre-generated thumbnails from thumbnails array
   * - Handles loading states and errors gracefully
   * - Supports different image presets (thumbnail, small, medium, large)
   * - Optimized for large documents with many pages
   *
   * Performance Benefits:
   * - Server-side image generation (much faster than client-side PDF.js)
   * - Pre-generated URLs (no API requests needed)
   * - Compressed images (smaller bandwidth usage)
   * - Scales perfectly to 1000+ page documents
   *
   * Performance Benefits:
   * - Multiple thumbnails from the same PDF document share a single cached PDF instance
   * - Significantly reduces network requests for documents with many pages
   * - LRU cache eviction prevents memory bloat
   *
   * @example
   * ```vue
   * <PDFPageThumbnail
   *   :thumbnails="documentThumbnails"
   *   :page-number="5"
   *   preset="thumbnail"
   * />
   * ```
   *
   * @typedef {Object} Props
   * @property {Array} thumbnails - The thumbnails array containing imgproxy URLs
   * @property {number} pageNumber - Page number to display thumbnail for (1-based)
   * @property {string} [preset='thumbnail'] - Image preset ('thumbnail', 'small', 'medium', 'large')
   *
   * @typedef {Object} Exposed
   * @property {boolean} isLoading - Whether the thumbnail is currently loading
   * @property {string|null} error - Error message if thumbnail loading failed
   * @property {string|null} thumbnailUrl - The current thumbnail URL
   */

  import { ref, computed } from 'vue'
  import { FileText } from 'lucide-vue-next'
  import { getPageThumbnailUrl } from '@/utils/documentImageHelpers'
  import type { ImageproxyImagePreset } from '@/types/generated'

  interface Props {
    thumbnails: ImageproxyImagePreset[]
    pageNumber: number
    preset?: 'thumbnail' | 'small' | 'medium' | 'large'
  }

  const props = withDefaults(defineProps<Props>(), {
    preset: 'thumbnail'
  })

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const thumbnailUrl = computed(() => {
    if (!props.thumbnails || props.pageNumber <= 0) return null
    return getPageThumbnailUrl(props.thumbnails, props.pageNumber, props.preset)
  })

  const onImageLoad = () => {
    isLoading.value = false
    error.value = null
  }

  const onImageError = () => {
    isLoading.value = false
    error.value = 'Failed to load thumbnail'
  }

  defineExpose({
    isLoading,
    error,
    thumbnailUrl
  })
</script>
